import { createPortal } from "react-dom";
import { Share2, X, Copy, Send, Check, Loader2 } from "lucide-react";

/**
 * SnapshotPreviewDrawer
 * ---------------------
 * Portalled confirm-before-share modal — renders the just-minted (or restored)
 * runway snapshot SVG at scale, exposes the signed URL with a copy row, and
 * hosts the CPA email form. All state is lifted; this component is pure UI.
 */
export function SnapshotPreviewDrawer({
  open,
  shareUrl,
  shareRemaining,
  shareExpiresAt,
  shareCopied,
  onClose,
  onCopy,
  emailTo,
  setEmailTo,
  emailNote,
  setEmailNote,
  emailSending,
  emailSentTo,
  emailRemaining,
  onSendEmail,
}) {
  if (!open || !shareUrl) return null;
  return createPortal(
    <div
      data-testid="runway-snapshot-drawer-overlay"
      className="fixed inset-0 z-[9700] flex items-end sm:items-center justify-center px-3 pb-4 sm:pb-0"
      style={{ background: "rgba(2,6,23,0.72)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="runway-snapshot-drawer"
        className="relative w-full max-w-2xl rounded-2xl overflow-hidden"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,23,42,0.98), rgba(2,6,23,0.99))",
          border: "1px solid rgba(6,182,212,0.55)",
          boxShadow:
            "0 30px 60px -18px rgba(6,182,212,0.55), 0 0 40px -20px rgba(16,185,129,0.4)",
          animation: "runway-drawer-in 0.32s cubic-bezier(0.22,1,0.36,1)",
        }}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-cyan-500/25">
          <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-cyan-300 flex items-center gap-1.5">
            <Share2 size={11} /> Snapshot Preview
            {shareRemaining !== null && (
              <span
                data-testid="runway-share-remaining"
                className="ml-2 px-2 py-0.5 rounded-full text-emerald-200 text-[9px]"
                style={{
                  background: "rgba(16,185,129,0.15)",
                  border: "1px solid rgba(16,185,129,0.4)",
                }}
              >
                {shareRemaining} left today
              </span>
            )}
            {emailRemaining !== null && (
              <span
                data-testid="runway-email-cap-remaining"
                className="ml-1 px-2 py-0.5 rounded-full text-[9px]"
                style={{
                  background:
                    emailRemaining === 0
                      ? "rgba(244,63,94,0.15)"
                      : "rgba(6,182,212,0.15)",
                  border:
                    emailRemaining === 0
                      ? "1px solid rgba(244,63,94,0.4)"
                      : "1px solid rgba(6,182,212,0.4)",
                  color: emailRemaining === 0 ? "#fda4af" : "#67e8f9",
                }}
              >
                {emailRemaining}/5 sends
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="runway-snapshot-close"
            aria-label="Close preview"
            className="text-slate-400 hover:text-white p-1 rounded"
          >
            <X size={16} />
          </button>
        </div>
        <div
          className="px-5 py-4 flex items-center justify-center"
          style={{ background: "rgba(2,6,23,0.5)" }}
        >
          <img
            src={shareUrl}
            alt="Runway snapshot preview"
            data-testid="runway-snapshot-image"
            className="w-full h-auto rounded-lg"
            style={{
              maxHeight: 320,
              border: "1px solid rgba(6,182,212,0.35)",
              boxShadow: "0 12px 28px -14px rgba(6,182,212,0.55)",
            }}
          />
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center gap-2">
            <input
              readOnly
              data-testid="runway-snapshot-url"
              value={shareUrl}
              onFocus={(e) => e.target.select()}
              className="flex-1 min-w-0 rounded-md px-3 py-2 text-[11px] font-mono text-cyan-100 bg-slate-900/70 border border-cyan-500/25 focus:outline-none focus:border-cyan-400"
            />
            <button
              onClick={onCopy}
              data-testid="runway-snapshot-copy"
              className="inline-flex items-center gap-1 h-9 px-3 rounded-md font-mono text-[10px] uppercase tracking-widest transition-transform hover:-translate-y-px"
              style={{
                background:
                  "linear-gradient(135deg, rgba(6,182,212,0.22), rgba(6,182,212,0.06))",
                border: "1px solid rgba(6,182,212,0.55)",
                color: shareCopied ? "#6ee7b7" : "#67e8f9",
              }}
            >
              {shareCopied ? (
                <>
                  <Check size={11} /> copied
                </>
              ) : (
                <>
                  <Copy size={11} /> copy
                </>
              )}
            </button>
          </div>
          <form
            onSubmit={onSendEmail}
            noValidate
            data-testid="runway-snapshot-email-form"
            className="space-y-2"
          >
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-slate-500">
              Or email direct to your CPA
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="cpa@firm.com"
                data-testid="runway-snapshot-email-input"
                className="flex-1 min-w-0 rounded-md px-3 py-2 text-[12px] font-mono text-slate-100 bg-slate-900/70 border border-slate-600/40 focus:outline-none focus:border-emerald-400 placeholder:text-slate-600"
                autoComplete="email"
              />
              <button
                type="submit"
                disabled={emailSending || emailRemaining === 0}
                data-testid="runway-snapshot-email-send"
                className="inline-flex items-center gap-1 h-9 px-3 rounded-md font-mono text-[10px] uppercase tracking-widest text-slate-950 transition-transform hover:-translate-y-px disabled:opacity-60"
                style={{
                  background: emailSentTo
                    ? "linear-gradient(135deg, #22d3ee, #06B6D4)"
                    : "linear-gradient(135deg, #10B981, #059669)",
                  boxShadow: "0 12px 26px -10px rgba(16,185,129,0.55)",
                }}
              >
                {emailSending ? (
                  <>
                    <Loader2 size={11} className="animate-spin" /> sending
                  </>
                ) : emailSentTo ? (
                  <>
                    <Check size={11} /> sent
                  </>
                ) : (
                  <>
                    <Send size={11} /> send
                  </>
                )}
              </button>
            </div>
            <textarea
              value={emailNote}
              onChange={(e) => setEmailNote(e.target.value.slice(0, 280))}
              data-testid="runway-snapshot-email-note"
              rows={2}
              placeholder="Optional note for your CPA (280 chars max)"
              className="w-full rounded-md px-3 py-2 text-[11px] font-mono text-slate-200 bg-slate-900/70 border border-slate-600/40 focus:outline-none focus:border-emerald-400/60 placeholder:text-slate-600 resize-none"
            />
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-600">
              Link expires {shareExpiresAt ? shareExpiresAt.slice(0, 10) : "in 7 days"}
              {emailSentTo && (
                <span
                  data-testid="runway-snapshot-email-status"
                  className="ml-2 text-emerald-300 normal-case tracking-normal"
                >
                  · sent to {emailSentTo}
                </span>
              )}
            </div>
          </form>
        </div>
      </div>
      <style>{`
        @keyframes runway-drawer-in {
          0%   { opacity: 0; transform: translateY(24px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0)   scale(1); }
        }
      `}</style>
    </div>,
    document.body
  );
}
